package com.tovtalk.data.network

import com.tovtalk.utils.logger.Logger
import kotlin.concurrent.Volatile
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

private const val TAG = "TT_VIDEO"

enum class VideoQuality(val key: String, val videoBitrateKbps: Int) {
    LOW("low", 1200),
    MID("mid", 2500),
    HIGH("high", 5000)
}

@OptIn(ExperimentalTime::class)
class BandwidthEstimator(
    private val maxSamples: Int = 10,
    private val minSamplesForDecision: Int = 3,
    private val minSampleBytes: Long = 50_000L
) {
    // Copy-on-write: [samples] is an immutable list swapped atomically. recordSample is
    // called concurrently from Ktor's onResponse (background threads) while
    // recommendedQuality iterates during feed mapping. Holding an immutable snapshot per
    // operation removes the ConcurrentModificationException / IndexOutOfBounds races that
    // a shared mutable list caused. A rare lost sample under concurrent writers is harmless
    // for a sliding-window bandwidth heuristic.
    @Volatile
    private var samples: List<BandwidthSample> = emptyList()

    fun recordSample(bytes: Long, durationMs: Long) {
        if (bytes < minSampleBytes || durationMs <= 0) return
        val bytesPerSecond = bytes.toDouble() / (durationMs.toDouble() / 1000.0)
        val kbps = (bytesPerSecond * 8) / 1000.0

        val updated = (samples + BandwidthSample(kbps, Clock.System.now().toEpochMilliseconds()))
            .let { if (it.size > maxSamples) it.takeLast(maxSamples) else it }
        samples = updated
        Logger.d("bandwidth_sample | kbps=${kbps.toInt()} | bytes=$bytes | duration_ms=$durationMs | total_samples=${updated.size}", TAG)
    }

    fun recommendedQuality(): VideoQuality {
        val snapshot = samples
        if (snapshot.size < minSamplesForDecision) {
            Logger.d("quality_decision | result=LOW (default) | reason=not_enough_samples (${snapshot.size}/$minSamplesForDecision)", TAG)
            return VideoQuality.LOW
        }

        var weightedSum = 0.0
        var totalWeight = 0.0
        snapshot.forEachIndexed { index, sample ->
            val weight = (index + 1).toDouble()
            weightedSum += sample.kbps * weight
            totalWeight += weight
        }
        val averageKbps = weightedSum / totalWeight

        val quality = when {
            averageKbps < LOW_MAX_KBPS -> VideoQuality.LOW
            averageKbps < MID_MAX_KBPS -> VideoQuality.MID
            else -> VideoQuality.HIGH
        }
        Logger.d("quality_decision | result=${quality.key} | avg_kbps=${averageKbps.toInt()} | samples=${snapshot.size} | thresholds=[LOW<${LOW_MAX_KBPS.toInt()}, MID<${MID_MAX_KBPS.toInt()}, HIGH≥${MID_MAX_KBPS.toInt()}]", TAG)
        return quality
    }

    companion object {
        const val LOW_MAX_KBPS = 1200.0
        const val MID_MAX_KBPS = 2500.0
    }
}

data class BandwidthSample(
    val kbps: Double,
    val timestamp: Long
)
